import { MRT_ColumnDef, MRT_PaginationState } from "material-react-table";
import { useState } from "react";
import { Box, Button, Chip, IconButton, Typography, Avatar } from "@mui/material";
import { Delete, Edit, RemoveRedEye } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { DataTable } from "../../libs/core/components/data-table";
import { Award } from "./types/award";
import { ConfirmModal } from "../../libs/ui/modal/confirm_modal";
import AddEditAward from "./components/AddEditAward";
import { useDeleteAwardMutation, useGetAwardsQuery } from "./api/awardApi";

const AwardManager: React.FC = () => {
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const [openDialog, setOpenDialog] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [selectedAward, setSelectedAward] = useState<Award | null>(null);
  const navigate = useNavigate();

  // Using RTK Query to fetch awards
  const { data: awards, isLoading, isError, isFetching } = useGetAwardsQuery();
  const [deleteAward] = useDeleteAwardMutation();

  const columns: Array<MRT_ColumnDef<Award>> = [
    {
      header: "Image",
      accessorKey: "images",
      size: 80,
      Cell({ row: { original } }) {
        const firstImage = original.images && original.images.length > 0 ? original.images[0] : null;
        return (
          <Avatar
            src={firstImage || undefined}
            alt={original.title}
            variant="rounded"
            sx={{ width: 56, height: 56 }}
          >
            {!firstImage && original.title?.charAt(0)?.toUpperCase()}
          </Avatar>
        );
      },
    },
    {
      header: "Title",
      accessorKey: "title",
      size: 150,
    },
    {
      header: "Year",
      accessorKey: "year",
      size: 80,
    },
    {
      header: "Status",
      accessorKey: "status",
      size: 100,
      Cell({ row: { original } }) {
        return <Chip label={original.status} color={original.is_active ? "success" : "default"} />;
      },
    },
    {
      header: "Nomination Deadline",
      accessorKey: "nomination_deadline",
      size: 150,
      Cell({ row: { original } }) {
        return <span>{original.nomination_deadline ? new Date(original.nomination_deadline).toLocaleDateString() : "-"}</span>;
      },
    },
  ];

  return (
    <Box>
      <DataTable<Award>
        toolbar={
          <Box display="flex" alignItems="center" width="100%">
            <Box display="flex" gap={3} alignItems="center" flex="1">
              <Typography variant="h6">Awards</Typography>
              <Chip
                color="primary"
                variant="outlined"
                label={`${awards?.data?.length ?? 0} Awards`} 
              />
            </Box>
            <Button
              onClick={() => {
                setSelectedAward(null);
                setOpenDialog(true);
              }}
              sx={{ minWidth: "200px" }}
              variant="contained"
            >
              Add New Award
            </Button>
          </Box>
        }
        list={awards?.data ?? []}
        count={awards?.data?.length ?? 0}
        columns={columns}
        getRowId={(row) => row?.id?.toString()}
        isLoading={isLoading}
        isSuccess={!isError}
        isError={isError}
        isFetching={isFetching}
        renderRowActions={(row) => (
          <Box display={"flex"} gap={1}>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setSelectedAward(row);
                setOpenConfirmModal(true);
              }}
            >
              <Delete />
            </IconButton>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setSelectedAward(row);
                setOpenDialog(true);
              }}
            >
              <Edit />
            </IconButton>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/awards/${row.id}`);
              }}
            >
              <RemoveRedEye />
            </IconButton>
          </Box>
        )}
        pagination={pagination}
        onPaginationChange={setPagination}
        onRowClick={(row) => navigate(`/awards/${row.id}`)}
        rowNumberMode={undefined}
      />

      {/* Add/Edit Award Dialog */}
      <AddEditAward 
        open={openDialog} 
        award={selectedAward}
        setModalOpen={setOpenDialog} 
      />

      {/* Confirm Delete Modal */}
      {selectedAward && (
        <ConfirmModal
          type="primary"
          title="Delete Award"
          message={`Are you sure you want to delete the award '${selectedAward.title}'?`}
          modalOpen={openConfirmModal}
          setModalOpen={setOpenConfirmModal}
          onOk={() => {
            deleteAward(selectedAward.id.toString());
            setOpenConfirmModal(false);
            setSelectedAward(null);
          }}
        />
      )}
    </Box>
  );
};

export default AwardManager; 